import Box from '@mui/material/Box';
import { GradientTypography } from '../gradients/GradientTypography';
import { FlatList } from './FlatList';
import { ReviewCard } from './ReviewListItem';
import { useTheme } from '@mui/material/styles';
import { GradientButton } from '../gradients/GradientButton';
import { Review } from 'functions/src/types/firestore/Game/Review';
import { ComingSoonTooltip } from '../coming-soon/ComingSoonTooltip';
import { useMobile } from 'src/hooks/useMobile';
import { Typography } from '@mui/material';

export type ReviewsListProps = {
  reviews: Review<Date>[];
  recommendationLevel: 'HIGHLY RECOMMENDED' | 'RECOMMENDED' | 'MIXED';
};

export function ReviewsList({
  reviews,
  recommendationLevel,
}: ReviewsListProps) {
  const theme = useTheme();
  const isMobile = useMobile();
  const consensusGradientColor =
    recommendationLevel === 'HIGHLY RECOMMENDED'
      ? 'success.vertical'
      : recommendationLevel === 'MIXED'
      ? 'warning.vertical'
      : 'error.vertical';
  return (
    <Box
      sx={{
        pb: 4,
        px: 2,
        maxWidth: !isMobile ? '640px' : 'auto',
        width: '100%',
      }}
    >
      <GradientTypography
        sx={{
          marginBottom: '8px',
          marginLeft: '10px',
          textTransform: 'uppercase',
        }}
        variant="h5"
        gradientColor="primary.vertical"
      >
        Reviews
      </GradientTypography>
      <Box
        sx={{
          borderRadius: '10px',
          position: 'relative',
          margin: 'auto',
          backgroundImage:
            'linear-gradient(rgba(13,31,50,1), rgba(13,31,50,1)), linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)',
          border: 'double 2px transparent',
          backgroundClip: 'content-box, border-box',
          backgroundOrigin: 'border-box',
          backdropFilter: theme.glass[4],
          boxShadow: theme.shadows[4],
          '&:hover': {
            boxShadow: theme.shadows[7],
          },
        }}
      >
        <Box
          sx={{
            background: theme.palette.background.elevation[2],
            borderRadius: '10px',
            py: 14,
            paddingBottom: 0,
            maxHeight: '840px',
            height: 'auto',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              background: theme.palette.background.elevation[2],
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              zIndex: 2,
              top: 0,
              left: 0,
              backgroundImage: `linear-gradient(${theme.palette.background.elevationSolid[9]}, 
                ${theme.palette.background.elevationSolid[8]}), 
                ${theme.palette.primary.horizontal}`,
              borderRadius: '10px 10px 0px 0px',
              borderBottom: 'solid 2px transparent',
              backgroundClip: 'content-box, border-box',
              backgroundOrigin: 'border-box',
            }}
          >
            <Box
              sx={{
                px: 4,
                py: 3,
              }}
            >
              <GradientTypography
                variant="h6"
                gradientColor={consensusGradientColor}
              >
                {recommendationLevel}
              </GradientTypography>
            </Box>
            <Box
              sx={{
                marginRight: '14px',
              }}
            >
              <ComingSoonTooltip title={'User reviews'}>
                <GradientButton
                  size="small"
                  sx={{ boxShadow: theme.shadowsHard.primaryElevation2 }}
                >
                  write a review
                </GradientButton>
              </ComingSoonTooltip>
            </Box>
          </Box>
          {!!reviews && reviews.length > 0 ? (
            <FlatList
              data={reviews.sort((a, b) => Number(b.date) - Number(a.date))}
              keyExtractor={({ title }) => title}
              divider
              showDividerOnLastItem={false}
              renderComponent={(params) => <ReviewCard {...params} />}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography variant="h5">No Reviews Yet</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
